import clsx from "clsx";
import CountUp from "react-countup";
import Sparkles from "react-sparkle";
import { CardForm } from "components/CardForm";
import { IconText } from "components/IconText";
import { DimensionDto } from "dtos/llm/DimensionDto";
import { Link } from "react-router-dom";
import { RouteLink } from "Routes";
import { GameTimer } from "components/game/GameTImer";

const scoreMultiple = 100;
const scoreOffset = -1;

const score1 = (1 + scoreOffset) * scoreMultiple;
const score2 = (2 + scoreOffset) * scoreMultiple;
const score3 = (3 + scoreOffset) * scoreMultiple;
const score4 = (4 + scoreOffset) * scoreMultiple;

const DisplayScoreAsGrade = (props: { llmScore: number; show: boolean }) => {
    const { llmScore, show } = props;

    const score = llmScore < 1.5 ? score1 : llmScore < 2.5 ? score2 : llmScore < 3.5 ? score3 : score4;

    if (!show) {
        return null;
    }

    return (
        <span
            className={clsx(
                "align-self-center",
                score === score1 && "text-danger",
                score === score2 && "text-primary",
                score === score3 && "text-warning",
                score === score4 && "text-success",
            )}
        >
            <CountUp end={score} duration={0.3} useEasing={false} />
        </span>
    );
};

const RatingStar = (props: { i: number; show: boolean; animationStage: number; thresholdShowStars: number }) => {
    const { i, show, animationStage, thresholdShowStars } = props;

    const animated = animationStage >= thresholdShowStars;

    return (
        <div
            className={clsx(
                "fas fa-star star position-relative",
                animated && "animated",
                `star--${i}`,
                show ? "star--correct" : "star-incorrect",
            )}
        >
            {show && animated && <Sparkles count={20} flickerSpeed="slowest" />}
        </div>
    );
};

const ExperimentalBadge = () => <span className="badge rounded-pill text-white bg-success">✨ Experimental</span>;

export const ReportCard = (props: {
    dimensions: Array<DimensionDto>;
    llmScore: number;
    animationStage: number;
    playerName: string;
}) => {
    const { dimensions, llmScore, animationStage, playerName } = props;

    const showFirstStar = llmScore >= 1.5;
    const showSecondStar = llmScore >= 2.5;
    const showThirdStar = llmScore >= 3.5;

    const thresholdShowStars = dimensions.length + 9;
    const thresholdDrumroll = dimensions.length + 7;

    const showTotal = animationStage > thresholdDrumroll + 1;

    const total = scoreMultiple * dimensions.reduce((prev, current) => prev + (current.llmScore + scoreOffset), 0);

    return (
        <div className="pb-5 pt-4 bg-dark text-white vstack position-relative shadow-lg border border-light">
            <div>
                <div className="ribbon h1">Scorecard</div>
            </div>

            <div className="vstack gap-3">
                <div className="h2 text-center">{playerName}</div>

                <div className="px-4 py-2">
                    <div className="vstack gap-3">
                        <div className="hstack justify-content-between gap-3 mx-auto text-white">
                            <RatingStar
                                i={1}
                                animationStage={animationStage}
                                thresholdShowStars={thresholdShowStars}
                                show={showFirstStar}
                            />

                            <RatingStar
                                i={2}
                                animationStage={animationStage}
                                thresholdShowStars={thresholdShowStars}
                                show={showSecondStar}
                            />

                            <RatingStar
                                i={3}
                                animationStage={animationStage}
                                thresholdShowStars={thresholdShowStars}
                                show={showThirdStar}
                            />
                        </div>

                        <div className="hstack gap-4 text-center lh-1 bg-gradient mx-5 px-3 py-3 border rounded-pill justify-content-center">
                            <div className="h2 text-end text-white">Score</div>

                            <div
                                style={{ width: 100 }}
                                className={clsx(
                                    "text-center h1 lh-1 animate",
                                    animationStage < thresholdDrumroll && "opacity-0",
                                    (animationStage === thresholdDrumroll ||
                                        animationStage === thresholdDrumroll + 1) &&
                                        "opacity-0",
                                    animationStage > thresholdDrumroll && "animate-bang",
                                    showTotal && [
                                        "opacity-100 animated",
                                        showThirdStar
                                            ? "text-success"
                                            : showSecondStar
                                            ? "text-warning"
                                            : showFirstStar
                                            ? "text-primary"
                                            : "text-danger",
                                    ],
                                )}
                            >
                                {showTotal && <CountUp end={total} duration={0.3} useEasing={false} separator="" />}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="hstack gap-4 justify-content-between align-self-center">
                    <div className="list-group gap-3 px-3">
                        {dimensions.map((dimension, index) => {
                            const showGrade = animationStage > index + 6;

                            return (
                                <div
                                    className="border border-white list-group-item px-4 py-1 rounded-pill hstack bg-dark justify-content-end gap-4 align-items-center bg-gradient"
                                    key={dimension.name}
                                >
                                    <div className="h5 py-2 text-end text-white">{dimension.name}</div>

                                    <div
                                        style={{ width: 80, height: 50 }}
                                        className={clsx(
                                            "justify-content-end h1 animate-bang d-flex flex-grow-0 flex-shrink-0",
                                            showGrade && "animated",
                                        )}
                                    >
                                        <DisplayScoreAsGrade show={showGrade} llmScore={dimension.llmScore} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="text-center">
                    <ExperimentalBadge />
                </div>
            </div>
        </div>
    );
};

export const ReportPhaseTwo = (props: {
    dimensions: Array<DimensionDto>;
    llmScore: number;
    animationStage: number;
    playerName: string;
    code: string;
    roundEnd?: number;
}) => {
    const { dimensions, llmScore, animationStage, playerName, code, roundEnd } = props;

    const showThirdStar = llmScore >= 3.5;

    const thresholdDrumroll = dimensions.length + 7;
    const showViewStory = animationStage > dimensions.length + 9;

    return (
        <div className="grid">
            <div className="g-col-12 g-col-lg-7">
                <ReportCard
                    dimensions={dimensions}
                    llmScore={llmScore}
                    animationStage={animationStage}
                    playerName={playerName}
                />
            </div>

            <div className="g-col-12 g-col-lg-5">
                <div className="vstack gap-4 ps-lg-5">
                    <div className={clsx("animate", showViewStory ? "opacity-100" : "opacity-0")}>
                        <CardForm
                            name="Game ending"
                            footer={
                                <Link
                                    to={RouteLink.gamesCompletedGame(code)}
                                    className={clsx(
                                        "btn btn-primary icon-link stretched-link",
                                        !showViewStory && "disabled",
                                    )}
                                >
                                    <IconText icon="fas fa-arrow-right" text="View story" />
                                </Link>
                            }
                        >
                            <div className="hstack">
                                <GameTimer state="Reporting" roundEnd={roundEnd} />
                            </div>
                        </CardForm>
                    </div>
                </div>

                {animationStage > thresholdDrumroll + 1 && showThirdStar && <div className="fireworks"></div>}
            </div>
        </div>
    );
};
